<template>
  <BasePopup
    ref="popup"
    class="subscribe-popup"
    :class="{ 'subscribe-popup--20504': treatment }"
    :compact="true"
    :fullscreen="fullscreen16218"
  >
    <SubscribePopupContent
      :book="resolvedInterestBook"
      :plan-nickname="plan.nickname"
      :treatment="treatment"
    />

    <div class="popup__inner">
      <form class="subscribe-popup__form" @submit="submitForm">
        <StripeCard
          v-if="visible && billing.cardEditMode"
          ref="stripeCard"
          :billing="billing"
          :show-button="false"
          @saved="onCardSaved"
        />
        <StripeCardInfo
          v-if="visible && !billing.cardEditMode"
          ref="stripeCardInfo"
          :billing="billing"
          @change="onChangeHandler"
        />
        <div class="subscribe-popup__cta">
          <v-wait for="Subscribing">
            <template v-slot:waiting>
              <span class="subscribe-popup__btn btns btns--large">
                Processing...
                <button-spinner />
              </span>
            </template>
            <a class="subscribe-popup__btn btns btns--large" @click="subscribe">
              {{ popupButtonText }}
            </a>
          </v-wait>
        </div>
        <div v-if="preauthAmountText" class="subscribe-popup__pre-auth-message">
          Upon entering your card details, we will authorize and
          <b><u>immediately refund</u></b> a charge of {{ preauthAmountText }}.
          The trial will still cost&nbsp;$0.
        </div>
        <FormError :errors="validation.errors" :field="plan.id" />
        <button type="submit" style="display: none" />
      </form>
    </div>
    <div v-if="!billing.isTrialOver" class="subscribe-popup__tips">
      <p>
        By clicking the "{{ popupButtonText }}" button above, you agree to our
        <router-link target="_blank" :to="{ name: 'site_terms' }"
          >Terms of Use</router-link
        >
        and
        <router-link target="_blank" :to="{ name: 'site_privacy' }"
          >Privacy Policy</router-link
        >.
        <span v-if="hasTrialDays"
          >You may cancel any time in your free trial and you will not be
          charged.
        </span>
        To cancel, go to Billing and click on "Cancel subscription."
        <br /><br />

        <strong v-if="hasTrialDays">
          Shortform will automatically continue your subscription at the end of
          your free trial on {{ trialEnd }} and
          {{ subscribePopupChargesText }}
        </strong>
        <strong v-else>
          Shortform will automatically continue your subscription and
          {{ subscribePopupChargesText }}
        </strong>

        There are no refunds or credits for partial subscription periods.
      </p>
    </div>
    <div v-else-if="billing.isTrialOver" class="subscribe-popup__tips">
      <p>
        After you click the "Subscribe" button above,
        <strong>Shortform will {{ subscribePopupChargesText }}</strong>
        You may cancel your subscription at any time. To cancel, go to Billing
        and click on "Cancel subscription." There are no refunds or credits for
        partial subscription periods.
        <br /><br />
      </p>
    </div>
  </BasePopup>
</template>

<script setup lang="ts">
import { computed, inject, ref } from 'vue'
import { useWait } from '@/app/composables/useWait'

import type { BillingView, PlanView } from '@/models/billing'
import { ValidationState } from '@/helpers/form'
import { interestBookKey, type InterestBook } from '@/helpers/billingSourceBook'
import { DEFAULT_INTEREST_BOOK } from '@/helpers/bookCoverStack'

import FormError from '@/components/ui/FormError.vue'
import ButtonSpinner from '@/components/ui/ButtonSpinner.vue'
import StripeCard from '@/app/components/billing/StripeCard.vue'
import StripeCardInfo from '@/app/components/billing/StripeCardInfo.vue'
import BasePopup from '@/components/ui/BasePopup.vue'
import SubscribePopupContent from '@/app/components/billing/SubscribePopupContent.vue'

import {
  checkoutModalSimplification20504Experiment,
  trialByPlanV2_18369Variant,
} from '@/services/ab'
import {
  getTrialPolicy,
  trialByPlanV2_18369Treatment,
} from '@/services/ab/18369_trial_by_plan'
import type { PlatformTrialPolicy } from '@/services/ab/18369_trial_by_plan'

const props = defineProps<{
  billing: BillingView
  plan: PlanView
  fullscreen16218?: boolean
}>()

const treatment = ref(false)

const wait = useWait()
const emit = defineEmits(['subscribed'])

const popup = ref<InstanceType<typeof BasePopup> | null>(null)
const stripeCard = ref<InstanceType<typeof StripeCard> | null>(null)

const visible = ref(false)
const validation = ref(new ValidationState())
const trialPolicy = ref<PlatformTrialPolicy | null>(null)

const interestBook = inject(interestBookKey, ref<InterestBook | null>(null))
const resolvedInterestBook = computed(
  () => interestBook.value ?? DEFAULT_INTEREST_BOOK,
)

const popupButtonText = computed(() => {
  if (props.billing.isTrialOver) {
    return 'Subscribe'
  }

  if (trialByPlanV2_18369Treatment()) {
    return props.plan.id === 'plan_annual'
      ? 'Start 14-day Free Trial'
      : 'Complete Payment'
  }

  return 'Start 5-day free trial'
})

async function show(): Promise<void> {
  const checkoutVariant = await checkoutModalSimplification20504Experiment()
  treatment.value = checkoutVariant === 'B'

  const variant = trialByPlanV2_18369Variant()
  if (variant) {
    const period = props.plan.id === 'plan_monthly' ? 'monthly' : 'annual'
    trialPolicy.value = await getTrialPolicy(variant, period)
  }
  visible.value = true
  popup.value?.show()
}

function hide(): void {
  visible.value = false
  validation.value.reset()
  popup.value?.hide()
}

function onChangeHandler(): void {
  props.billing.cardEditMode = true
  validation.value.reset()
}

/**
 * Subscribe user after the card save.
 *
 * We call `subscribe()` after the card save so the user doesn't have to click
 * "Start Free Trial" again after saving card details or using the express checkout
 * element.
 * The issue is described in this comment:
 * https://github.com/shortformhq/main/issues/17363#issuecomment-3977145423
 */
async function onCardSaved(): Promise<void> {
  props.billing.cardEditMode = false

  if (wait.is('Subscribing')) {
    return
  }

  await subscribe()
}

async function submitForm(event: Event): Promise<void> {
  event.preventDefault()
  await subscribe()
}

const hasTrialDays = computed(() => {
  if (!trialPolicy.value) {
    return true
  }
  return trialPolicy.value.trial_days !== null
})

const trialEnd = computed(() => {
  return props.billing.trialEnd(props.plan.id)
})

async function subscribe(): Promise<void> {
  try {
    let cardSuccess = true
    wait.start('Subscribing')
    if (props.billing.cardEditMode && stripeCard.value?.needsSaving()) {
      cardSuccess = await stripeCard.value?.save()
    }
    if (cardSuccess) {
      await props.billing.subscribeTo(props.plan)
      emit('subscribed')
      validation.value.reset()
      hide()
    }
    wait.end('Subscribing')
  } catch (error) {
    wait.end('Subscribing')
    validation.value.showErrors(error)
  }
}

const subscribePopupChargesText = computed(() => {
  if (props.plan.nickname === 'Annual' && props.plan.discount) {
    return (
      `charge the subscription fee of ${props.plan.price.perPeriod} to your payment ` +
      `method for your first ${props.plan.labels.period}, ` +
      `then ${props.plan.priceNoDiscount.perPeriod} ` +
      `for subsequent ${props.plan.labels.period}s on ${props.plan.labels.basis} basis until you cancel.`
    )
  }

  return (
    `charge the subscription fee of ${props.plan.price.perPeriod} ` +
    `to your payment method on ${props.plan.labels.basis} basis until you cancel.`
  )
})

const preauthAmountText = computed(() => {
  return props.billing.preauthAmountText(props.plan)
})

defineExpose({
  show,
  hide,
})
</script>

<style lang="scss" scoped>
.subscribe-popup {
  --popup-width: 480px;

  // Untitled header: drop the titled-header border/spacing, and keep the
  // close button pinned to the top-right of the window.
  :deep(.popup__header) {
    margin-bottom: 0;
    border-bottom: none;
  }

  :deep(.popup__close) {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 1;
  }

  :deep(.popup__window) {
    overflow: auto;
  }

  &__btn {
    font-weight: 500;
  }

  &__tips {
    padding-top: 28px;
    margin-top: 28px;
    border-top: 1px solid $c-border-4;

    p {
      font-size: 11px;
      line-height: 20px;
      color: $c-black-2;
    }
  }

  &__pre-auth-message {
    width: 100%;
    background-color: $c-yellow-8;
    border-radius: 10px;
    padding: 16px 20px;
    font-size: 11px;
    color: $c-black-3;
    line-height: 1.5;
    margin: 16px auto;
  }

  &--20504 {
    .subscribe-popup__cta {
      margin-top: 4px;
    }

    .subscribe-popup__btn {
      width: 100%;
      padding: 18px 26px 16px;
      border-radius: 50px;
      background-color: $c-orange;
      border-color: $c-orange;
      color: $c-white;
      font-size: 18px;
      font-weight: 400;

      @include hover {
        background-color: $c-orange-2;
        border-color: $c-orange-2;
        color: $c-white;
      }

      &:focus {
        background-color: $c-orange-2;
        border-color: $c-orange-2;
        color: $c-white;
      }
    }

    .subscribe-popup__tips {
      margin-top: 16px;
      padding-top: 0;
      border-top: none;

      p,
      a {
        color: $c-black-3;
      }
    }
  }
}

.night-mode {
  .subscribe-popup {
    &__tips {
      border-top-color: $c-dark-5;

      p {
        color: $c-black-11;
      }
    }

    &--20504 {
      .subscribe-popup__tips {
        p,
        a {
          color: $c-black-11;
        }
      }
    }
  }
}
</style>
